import React from "react";
import {
  useReactTable,
  flexRender,
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  Row,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Stack,
  Tooltip,
} from "@mui/material";

import { TableVirtuoso, TableComponents } from "react-virtuoso";

import Server from "../types/Server";

import EccChip from "./EccChip";
import { grossPrice, formatEuro, diskSummary, serverKey, PROVIDER_NAMES } from "../utils/servers";

type ServerTableProps = {
  servers: Server[];
  onSelect: (server: Server) => void;
};

type TableContext = { onSelect: (server: Server) => void };

const columnHelper = createColumnHelper<Server>();

const diskTotal = (sizes: number[]) => sizes.reduce((sum, size) => sum + size, 0);

const columns = [
  columnHelper.group({
    id: "server-data",
    header: "Server Data",
    columns: [
      columnHelper.accessor((server) => PROVIDER_NAMES[server.provider], {
        id: "provider",
        header: "Provider",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("cpu", {
        header: "CPU",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("ram_size", {
        header: "RAM",
        cell: (info) => (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <span>{info.getValue()} GB</span>
            {info.row.original.is_ecc && <EccChip />}
          </Stack>
        ),
      }),
      columnHelper.accessor((server) => grossPrice(server), {
        id: "price",
        header: () => (
          <Tooltip title="Monthly, incl. 19% VAT & one IPv4">
            <span>Price</span>
          </Tooltip>
        ),
        cell: (info) => formatEuro(info.getValue()),
      }),
      columnHelper.accessor("datacenter", {
        header: "Datacenter",
        cell: (info) => info.getValue(),
      }),
    ],
  }),
  columnHelper.group({
    id: "disk-data",
    header: "Disks",
    columns: [
      columnHelper.accessor((server) => diskTotal(server.serverDiskData.nvme), {
        id: "nvme",
        header: "NVMe",
        cell: (info) => diskSummary(info.row.original.serverDiskData.nvme),
      }),
      columnHelper.accessor((server) => diskTotal(server.serverDiskData.sata), {
        id: "sata",
        header: "SATA",
        cell: (info) => diskSummary(info.row.original.serverDiskData.sata),
      }),
      columnHelper.accessor((server) => diskTotal(server.serverDiskData.hdd), {
        id: "hdd",
        header: "HDD",
        cell: (info) => diskSummary(info.row.original.serverDiskData.hdd),
      }),
    ],
  }),
];

// Column widths must live in a <colgroup>: with tableLayout fixed the browser
// only reads widths from the first header row, which is the colSpan group row.
const COLUMN_WIDTHS = ["10%", "21%", "12%", "10%", "13%", "11%", "11%", "12%"];

const VirtuosoTableComponents: TableComponents<Row<Server>, TableContext> = {
  // overflow visible on large screens: any overflow value would make the
  // container the sticky header's scroll context and the header would not
  // stick to the window scroll. Below lg the table is wider than the column,
  // so horizontal scrolling wins over stickiness there.
  Scroller: React.forwardRef<HTMLDivElement>((props, ref) => (
    <TableContainer
      component={Paper}
      {...props}
      ref={ref}
      sx={{ overflowX: { xs: "auto", lg: "visible" } }}
    />
  )),
  Table: ({ children, ...props }) => (
    <Table
      {...props}
      stickyHeader
      size="small"
      sx={{ tableLayout: "fixed", minWidth: 840 }}
    >
      <colgroup>
        {COLUMN_WIDTHS.map((width, index) => (
          <col key={index} style={{ width }} />
        ))}
      </colgroup>
      {children}
    </Table>
  ),
  TableHead: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
    <TableHead {...props} ref={ref} />
  )),
  TableRow: ({ item, context, ...props }) => (
    <TableRow
      {...props}
      hover
      onClick={() => context?.onSelect(item.original)}
      sx={{ cursor: "pointer" }}
    />
  ),
  TableBody: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
    <TableBody {...props} ref={ref} />
  )),
};

function rowContent(_index: number, row: Row<Server>) {
  return (
    <React.Fragment>
      {row.getVisibleCells().map((cell) => (
        <TableCell key={cell.id}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ))}
    </React.Fragment>
  );
}

const ServerTable: React.FC<ServerTableProps> = ({ servers, onSelect }) => {
  const [sorting, setSorting] = React.useState<SortingState>([{ id: "price", desc: false }]);

  const table = useReactTable({
    data: servers,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const fixedHeaderContent = () => (
    <>
      {table.getHeaderGroups().map((headerGroup) => (
        <TableRow key={headerGroup.id}>
          {headerGroup.headers.map((header) => (
            <TableCell
              key={header.id}
              colSpan={header.colSpan}
              sortDirection={header.column.getIsSorted()}
            >
              {header.isPlaceholder ? null : header.column.getCanSort() ? (
                <TableSortLabel
                  active={header.column.getIsSorted() !== false}
                  direction={header.column.getIsSorted() || "asc"}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableSortLabel>
              ) : (
                flexRender(header.column.columnDef.header, header.getContext())
              )}
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );

  return (
    <TableVirtuoso
      data={table.getRowModel().rows}
      context={{ onSelect }}
      components={VirtuosoTableComponents}
      computeItemKey={(_index, row) => serverKey(row.original)}
      fixedHeaderContent={fixedHeaderContent}
      itemContent={rowContent}
      useWindowScroll
    />
  );
};

export default ServerTable;
